import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { AnsprechpartnerMitHerkunft, KundeListe, TicketBezugsKandidaten, TicketDetail, Unternehmen }
  from "@kundeninfo/api-client";
import { useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Auswahl, Chip, Hinweis } from "@kundeninfo/ui/me-design";
import { Dateiwahl, KiKnopf, LoeschKnopf, Suchfeld } from "../../components/meHuellen.js";
import { ObjektVerweis } from "../../components/objektPanel/ObjektVerweis.js";
import { bezugAusAkteZiel } from "../../components/objektPanel/objektBezug.js";
import type { ObjektBezug } from "../../components/objektPanel/objektBezug.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";
import { JEDE_ROLLE } from "../partners/rollenFilter.js";
import { REFERENCE_KINDS, REFERENCE_LABEL_KIND, REFERENCE_TAB, REFERENCE_TEXT } from "./referenceTexts.js";

/**
 * Reiter des Ticket-Dialogs: Kunden und Bezüge, Anhänge (der Verlauf steht in
 * TicketVerlauf.tsx — die Datei wäre sonst über 400 Zeilen gewachsen). Jeder Schreibweg läuft
 * über `useSchreibweg` (Ruling B7): volles Detail → `onNeu`, 409 → nachladen + Meldung,
 * 422 → Befund am Feld, sonst Hinweis im Reiter.
 */

export type ReiterBasis = {
  d: TicketDetail; client: TicketsClient; onNeu: (d: TicketDetail) => void;
  /** Holt das Detail neu (409 eines Unterobjekts). */
  nachladen: () => Promise<TicketDetail>;
};

export { VerlaufReiter } from "./TicketVerlauf.js";

// ------------------------------------------------------------------ Kunden und Bezüge (F42)

/** Der Bezug als Objekt (K6, PF20): Reiter seiner Art, Vorgang, Unternehmen des Tickets (ein Bezug gehört zu ihm, Aufgabe
 *  20). Die Beschriftung ohne die vorangestellte Art — der Verweis nennt sie selbst; eine Datei springt in die Akte ihres
 *  Kunden und heißt nach ihm („Zur Akte …“), in den Ordner, in dem sie liegt (`folderId` vom Server, Entscheid 713). */
function referenceBezug(r: TicketDetail["teile"]["references"][number], companyId: string): ObjektBezug {
  const art = REFERENCE_LABEL_KIND[r.kind];
  const label = art === null ? r.customerName : r.label.startsWith(`${art} `) ? r.label.slice(art.length + 1) : r.label;
  return bezugAusAkteZiel({ customerId: r.customerId, tab: REFERENCE_TAB[r.kind], objectId: r.targetId, companyId,
    ...(r.kind === "FILE" ? { folderId: r.folderId } : {}) }, label);
}


export function KundenReiter(p: ReiterBasis & {
  companies: readonly Unternehmen[];
  /** Sieht der Benutzer Lieferantendaten? Dann nennt die Bezugswahl auch Verträge und Eingangsrechnungen (Aufgabe 22). */
  supplierView?: boolean;
}): ReactElement {
  const supplier = p.supplierView === true;
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const [bezugSuche, setBezugSuche] = useState("");
  const [kandidaten, setKandidaten] = useState<TicketBezugsKandidaten["items"]>([]);
  // Zu welchem Suchtext die Kandidaten geladen sind (null: noch keine Antwort) — der Leertext
  // gilt erst nach einer Antwort, nicht schon beim leeren Suchfeld (Sichtprobe 12).
  const [geladenFuer, setGeladenFuer] = useState<string | null>(null);
  const [kontakte, setKontakte] = useState<AnsprechpartnerMitHerkunft[]>([]);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen });
  const { bestaetigen } = useBestaetigung();
  const sucheNr = useRef(0);
  const kundenSchluessel = p.d.customers.map((c) => c.id).join(",");
  const { client } = p;
  const ticketId = p.d.id;

  // Ansprechpartner der zugeordneten Kunden (nur aktive) für die Auswahl — ohne die geerbten der
  // Konzernkette: Deren Kunde steht nicht am Ticket, der Server wiese sie ab (Gesamtreview I2).
  useEffect(() => {
    let aktiv = true;
    const ids = kundenSchluessel === "" ? [] : kundenSchluessel.split(",");
    Promise.all(ids.map((id) => client.listContacts(id)))
      .then((alle) => { if (aktiv) setKontakte(alle.flatMap((r) => r.items).filter((k) => k.status === "ACTIVE" && !k.inherited)); })
      .catch(() => { if (aktiv) setKontakte([]); });
    return () => { aktiv = false; };
  }, [client, kundenSchluessel]);

  // Bezugs-Kandidaten: je Art die neuesten zuerst (Server, höchstens 20 je Art) — nur mit Kunde.
  useEffect(() => {
    if (kundenSchluessel === "") { setKandidaten([]); setGeladenFuer(null); return undefined; }
    let aktiv = true;
    const text = bezugSuche.trim();
    client.searchTicketReferenceCandidates(ticketId, text)
      .then((r) => { if (aktiv) { setKandidaten(r.items); setGeladenFuer(text); } })
      .catch(() => { if (aktiv) { setKandidaten([]); setGeladenFuer(null); } });
    return () => { aktiv = false; };
  }, [client, ticketId, kundenSchluessel, bezugSuche]);

  async function kundenSuchen(text: string): Promise<void> {
    setSuche(text);
    const nr = ++sucheNr.current;
    if (text.trim().length < 2) { setTreffer([]); return; }
    try {
      // Über das Unternehmen des TICKETS, nicht das aktive (E363: Tickets gehen über alle Unternehmen).
      const r = await client.getCustomers({ search: text.trim(), limit: 20, companyIds: [p.d.companyId], ...JEDE_ROLLE });
      if (nr !== sucheNr.current) return;
      setTreffer(r.items.filter((k) => !p.d.customers.some((c) => c.id === k.id)));
    } catch {
      if (nr === sucheNr.current) setTreffer([]);
    }
  }

  async function zuordnen(kundeId: string): Promise<void> {
    if (await weg.schreiben(() => client.addTicketCustomer(ticketId, kundeId), "Der Kunde ließ sich nicht zuordnen.")) {
      setSuche(""); setTreffer([]);
    }
  }

  async function loesen(kundeId: string, name: string): Promise<void> {
    // Kein Lösen ohne Vorschau (F42): Was mit dem Kunden wegfällt, muss vorher dastehen.
    let mit: { contacts: { id: string; name: string }[]; references: { id: string; label: string }[] };
    try {
      mit = await client.previewRemoveTicketCustomer(ticketId, kundeId);
    } catch {
      weg.melden({ art: "fehler", text: `Was mit ${name} wegfiele, ließ sich nicht ermitteln — der Kunde bleibt zugeordnet.` });
      return;
    }
    weg.melden(null);
    // Die Bestätigung ist ein Satz reiner Text (me-design `bestaetigen()`): was wegfällt, steht als
    // Aufzählung im Satz statt als Liste.
    const wegfall = [...mit.contacts.map((c) => `Ansprechpartner ${c.name}`), ...mit.references.map((r) => `Bezug ${r.label}`)];
    const text = wegfall.length === 0
      ? REFERENCE_TEXT.unlinkText(name)
      : `${REFERENCE_TEXT.unlinkText(name)} Mit dem Kunden fallen weg: ${wegfall.join(", ")}.`;
    if (!(await bestaetigen({ titel: REFERENCE_TEXT.unlinkCustomerTitle, text,
      bestaetigenText: REFERENCE_TEXT.unlink }))) return;
    await weg.schreiben(() => client.removeTicketCustomer(ticketId, kundeId), "Der Kunde ließ sich nicht lösen.");
  }

  /** Ansprechpartner und Bezug lösen — wie der Kunde erst nach der Rückfrage (Hausregel 19.10.2026), aber ohne
   *  Vorschau: An ihnen hängt nichts, was mit wegfiele. */
  async function teilLoesen(titel: string, name: string, schreiben: () => Promise<TicketDetail>, fehler: string)
    : Promise<void> {
    if (!(await bestaetigen({ titel, text: REFERENCE_TEXT.unlinkText(name), bestaetigenText: REFERENCE_TEXT.unlink }))) {
      return;
    }
    await weg.schreiben(schreiben, fehler);
  }

  const freieKontakte = kontakte.filter((k) => !p.d.teile.contacts.some((c) => c.id === k.id));
  const freieKandidaten = kandidaten.filter((k) => !p.d.teile.references.some((r) => r.kind === k.kind && r.targetId === k.targetId));

  return (
    <div className="td__kunden">
      <h4 className="td__ueberschrift">Kunden</h4>
      {p.d.customers.length === 0 && <p className="td__leer">Ohne Kunde (internes Ticket).</p>}
      <ul className="td__liste">
        {/* K5: der Kunde ist ein fremdes Objekt — Zugehörigkeit als Kennzeichen, Name als Text, der Sprung in den Reiter
            Tickets seiner Akte direkt dahinter, „Lösen“ am Zeilenende (115). */}
        {p.d.customers.map((c) => (
          <li key={c.id} className="td__nennung">
            <Chip {...hausfarbeChip(p.companies, p.d.companyShortCode)}>{p.d.companyShortCode}</Chip>
            <span>{c.name}</span>
            <ObjektVerweis bezug={{ art: "akte", id: c.id, reiter: "tickets", label: c.name }} />
            <LoeschKnopf name={`${c.name} lösen`} deaktiviert={weg.laeuft} onClick={() => { void loesen(c.id, c.name); }} />
          </li>
        ))}
      </ul>
      <Suchfeld name="Kunde suchen" platzhalter="Kunde zuordnen (mindestens 2 Zeichen)" wert={suche}
        onText={(t) => { void kundenSuchen(t); }} />
      {treffer.length > 0 && (
        <ul className="td__liste">
          {treffer.map((k) => (
            <li key={k.id}><KiKnopf art="leise" deaktiviert={weg.laeuft} onClick={() => { void zuordnen(k.id); }}>
              {`${k.companyName} zuordnen`}</KiKnopf></li>
          ))}
        </ul>
      )}

      <h4 className="td__ueberschrift">Ansprechpartner</h4>
      {p.d.teile.contacts.length === 0 && <p className="td__leer">Keine Ansprechpartner zugeordnet.</p>}
      <ul className="td__liste">
        {p.d.teile.contacts.map((c) => (
          <li key={c.id}>{c.name}{c.status === "LEFT" ? " (ausgeschieden)" : ""}
            <LoeschKnopf name={`${c.name} lösen`} deaktiviert={weg.laeuft} onClick={() => {
              void teilLoesen(REFERENCE_TEXT.unlinkContactTitle, c.name, () => client.removeTicketContact(ticketId, c.id),
                "Der Ansprechpartner ließ sich nicht lösen.");
            }} /></li>
        ))}
      </ul>
      {p.d.customers.length > 0 && (
        <Auswahl beschriftung="Ansprechpartner zuordnen" wert="" gesperrt={weg.laeuft} onGeaendert={(e) => {
          if (e.wert !== "") {
            void weg.schreiben(() => client.addTicketContact(ticketId, e.wert), "Der Ansprechpartner ließ sich nicht zuordnen.");
          }
        }}>
          <option value="">{freieKontakte.length === 0 ? "– keine weiteren –" : "– wählen –"}</option>
          {freieKontakte.map((k) => <option key={k.id} value={k.id}>{k.anzeigename}</option>)}
        </Auswahl>
      )}

      <h4 className="td__ueberschrift">Bezüge</h4>
      {p.d.teile.references.length === 0 && <p className="td__leer">Keine Bezüge.</p>}
      <ul className="td__liste">
        {/* K6: der Bezug ist ein fremdes Objekt — Beschriftung als Text, der Verweis direkt dahinter (Liefervertrag und
            Eingangsrechnung mit Panel, Vorgänge und Dateien nur Sprung; Rechte entscheidet der Gastgeber, nicht `navigable`),
            der Kunde leise, „Lösen“ am Zeilenende (115). */}
        {p.d.teile.references.map((r) => (
          <li key={r.id} className="td__nennung">
            <span>{r.label}{r.hinweis !== null ? ` (${r.hinweis})` : ""}</span>
            <ObjektVerweis bezug={referenceBezug(r, p.d.companyId)} />
            <span className="td__neben">{r.customerName}</span>
            <LoeschKnopf name={`${r.label} lösen`} deaktiviert={weg.laeuft} onClick={() => {
              void teilLoesen(REFERENCE_TEXT.unlinkReferenceTitle, r.label, () => client.removeTicketReference(ticketId, r.id),
                "Der Bezug ließ sich nicht lösen.");
            }} />
          </li>
        ))}
      </ul>
      {p.d.customers.length === 0 ? (
        <Hinweis art="info">{REFERENCE_TEXT.customerFirst(supplier)}</Hinweis>
      ) : (
        <>
          <Suchfeld name={REFERENCE_TEXT.searchName} platzhalter={REFERENCE_TEXT.searchPlaceholder(supplier)} wert={bezugSuche}
            onText={setBezugSuche} hilfe={REFERENCE_TEXT.searchHelp} />
          {geladenFuer !== null && freieKandidaten.length === 0 && (geladenFuer !== "" || kandidaten.length === 0) && (
            <p className="td__leer">{geladenFuer !== "" ? REFERENCE_TEXT.noMatches : REFERENCE_TEXT.noDocuments}</p>
          )}
          {REFERENCE_KINDS.map((art) => {
            const gruppe = freieKandidaten.filter((k) => k.kind === art);
            if (gruppe.length === 0) return null;
            const titel = REFERENCE_TEXT.groupTitle[art];
            return (
              <section key={art} className="td__kandidaten" aria-label={titel}>
                <h5 className="td__gruppe">{titel}</h5>
                <ul className="td__liste">
                  {gruppe.map((k) => (
                    <li key={`${k.kind}-${k.targetId}`}>
                      <KiKnopf art="leise" name={`${k.label} verknüpfen`} deaktiviert={weg.laeuft} onClick={() => {
                        void weg.schreiben(() => client.addTicketReference(ticketId, k.kind, k.targetId),
                          "Der Bezug ließ sich nicht verknüpfen.");
                      }}>{k.label}</KiKnopf>
                      <span className="td__neben">{k.customerName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
      <SchreibHinweis meldung={weg.meldung} />
    </div>
  );
}

// ------------------------------------------------------------------ Anhänge

function groesse(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** F121: Dateiname gekürzt mit Auslassung, der volle Name im Sofort-Popup (Hausregel statt `title`). */
function AnhangName(p: { name: string; href: string }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(p.name);
  return (
    <span ref={popup} className="td__anhangname">
      <KiKnopf art="leise" href={p.href} download={p.name}>{p.name}</KiKnopf>
    </span>
  );
}

export function AnhaengeReiter(p: ReiterBasis): ReactElement {
  const [laedt, setLaedt] = useState<string[]>([]);
  const weg = useSchreibweg(p.onNeu, { nachladen: p.nachladen });
  const { bestaetigen } = useBestaetigung();

  /** Nacheinander hochladen; Fehler je Datei sammeln — ein späterer Erfolg löscht keinen. */
  async function hochladen(dateien: File[]): Promise<void> {
    setLaedt(dateien.map((f) => f.name));
    const fehler: string[] = [];
    try {
      for (const f of dateien) {
        const e = await weg.versuchen(() => p.client.uploadTicketAttachment(p.d.id, f), "Das Hochladen ist fehlgeschlagen.");
        if (e.meldung !== null) fehler.push(`„${f.name}“: ${e.meldung.text}`);
      }
    } finally {
      setLaedt([]);
      weg.melden(fehler.length === 0 ? null : { art: "fehler", text: fehler.join("\n") });
    }
  }

  return (
    <div className="td__anhaenge">
      <Dateiwahl name="Datei hochladen" knopftext="Hochladen" mehrere nachWahlLeeren gesperrt={weg.laeuft}
        hilfe="Höchstens 50 MB je Datei." onDateien={(dateien) => { void hochladen(dateien); }} />
      {laedt.length > 0 && <Hinweis art="info">{`Lädt hoch: ${laedt.join(", ")} …`}</Hinweis>}
      {p.d.teile.attachments.length === 0 && <p className="td__leer">Noch keine Anhänge.</p>}
      <ul className="td__liste">
        {p.d.teile.attachments.map((a) => (
          <li key={a.id} className="td__anhang">
            <AnhangName name={a.originalName} href={p.client.ticketAttachmentUrl(p.d.id, a.id)} />
            <span className="td__neben">{`${groesse(a.sizeBytes)} · ${a.uploadedBy?.name ?? "–"}`}</span>
            <LoeschKnopf name={`${a.originalName} löschen`} deaktiviert={weg.laeuft} onClick={() => { void (async () => {
              if (!(await bestaetigen({ titel: "Anhang löschen?", text: `„${a.originalName}“ wird endgültig gelöscht.`,
                                        bestaetigenText: "Löschen", gefaehrlich: true }))) return;
              await weg.schreiben(() => p.client.deleteTicketAttachment(p.d.id, a.id), "Der Anhang ließ sich nicht löschen.");
            })(); }} />
          </li>
        ))}
      </ul>
      <SchreibHinweis meldung={weg.meldung} />
    </div>
  );
}
